import { useEffect, type ReactNode } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useAuthStore } from '@/store/authStore';

/**
 * AuthProvider — listens to Firebase sign-in / sign-out and ID token refreshes.
 * Token refreshes (e.g. getIdToken(true) after the email is verified) re-run
 * setUser, so isAuthorized picks up the new emailVerified value.
 * Mirrors Flutter's FirebaseAuth.instance.currentUser checks in routing_config.dart.
 *
 * Must mount AFTER TenantProvider so that auth.tenantId is set.
 *
 * Children render only once Firebase has restored the persisted session (first
 * onIdTokenChanged event), so pages that read auth.currentUser on mount and the
 * route guards see the real user — as Flutter does, where currentUser is resolved
 * before the first route is built.
 */

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const setUser = useAuthStore((s) => s.setUser);
  const setAuthLoading = useAuthStore((s) => s.setAuthLoading);
  const isAuthLoading = useAuthStore((s) => s.isAuthLoading);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
    });

    return unsubscribe;
  }, [setUser, setAuthLoading]);

  if (isAuthLoading) return null;

  return <>{children}</>;
}
